// oxlint-disable react/no-unstable-nested-components -- `itemComponent` is Kobalte's render-prop API for list items; defining it inline is the idiomatic usage.
import { Select as SelectPrimitive } from "@kobalte/core/select";
import { Check, ChevronDown, Plus } from "@fleet/icons";
import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@gymlog/shared";

import { overlayPortalHost } from "./overlay-portal";

/**
 * CTA shown inside the dropdown when Select has no options
 * (AWS style: an empty combobox offers "Create X" / "Go to X" instead of a
 * dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or
 * `href` to render a link (navigate to the screen that provides the
 * options). `can` (default `true`) gates the CTA by permission — if it is `false`
 * the empty dropdown falls back to a "no options" message with no creation affordance.
 * Omit `emptyAction` entirely when the source cannot be created.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Default true. */
  can?: boolean;
  /** Leading icon for the CTA. Default: a plus glyph. */
  icon?: JSX.Element;
  /** Optional helper line above the CTA (e.g. "No routines yet"). */
  description?: string;
}

/**
 * Premium Select on top of `@kobalte/core` — same visual language as
 * popover/dialog (repo tokens, animated panel, theme-safe). Supports
 * keyboard, typeahead, and built-in ARIA via Kobalte.
 *
 * Usage:
 * ```tsx
 * <Select
 *   value={value()}
 *   onChange={(v) => v && setValue(v)}
 *   options={["a", "b"]}
 *   itemLabel={(v) => labelOf(v)}
 *   placeholder="Pick one"
 *   aria-label="Example"
 * />
 * ```
 */
export interface SelectProps<T> {
  value: T | null;
  onChange: (value: T | null) => void;
  options: readonly T[];
  /** Visible label for each option (defaults to `String(option)`). */
  itemLabel?: (option: T) => JSX.Element;
  placeholder?: JSX.Element;
  disabled?: boolean;
  class?: string;
  /** Panel width; defaults to matching the trigger. */
  contentClass?: string;
  /** Needed for a11y when there is no associated <Label>. */
  "aria-label"?: string;
  id?: string;
  name?: string;
  "data-testid"?: string;
  /**
   * Displayed inside the dropdown when `options` is empty. Allows jumping to
   * the place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message for the empty dropdown when there is no CTA (allowed). */
  emptyLabel?: string;
}

export function Select<T>(props: SelectProps<T>) {
  const [local] = splitProps(props, [
    "value",
    "onChange",
    "options",
    "itemLabel",
    "placeholder",
    "disabled",
    "class",
    "contentClass",
    "aria-label",
    "id",
    "name",
    "data-testid",
    "emptyAction",
    "emptyLabel",
  ]);

  const label = (option: T): JSX.Element =>
    local.itemLabel ? local.itemLabel(option) : (String(option) as JSX.Element);

  const isEmpty = () => local.options.length === 0;

  return (
    <SelectPrimitive<T>
      value={local.value}
      // Single-select required: re-clicking the ALREADY selected option must NOT
      // toggle it to empty. By default Kobalte emits `null` when re-clicking the
      // current selection (deselect) → the field was left empty before submit
      // (e.g. re-selecting the same exercise/routine). With `disallowEmptySelection`
      // the value can CHANGE to another option but cannot be emptied by interaction.
      // (CC: Re-clicking Select deselects.)
      disallowEmptySelection
      onChange={local.onChange}
      options={[...local.options]}
      disabled={local.disabled}
      optionTextValue={(o) => {
        const l = label(o);
        return typeof l === "string" ? l : String(o);
      }}
      placeholder={local.placeholder}
      itemComponent={(itemProps) => (
        <SelectPrimitive.Item
          item={itemProps.item}
          class={cn(
            "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-3 py-2 text-sm outline-none transition-colors",
            "text-popover-foreground",
            "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
            "data-[selected]:font-semibold",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        >
          <SelectPrimitive.ItemLabel class="truncate">
            {label(itemProps.item.rawValue)}
          </SelectPrimitive.ItemLabel>
          <SelectPrimitive.ItemIndicator class="shrink-0">
            <Check class="h-4 w-4" />
          </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
      )}
    >
      <SelectPrimitive.HiddenSelect id={local.id} name={local.name} />
      <SelectPrimitive.Trigger
        aria-label={local["aria-label"]}
        data-testid={local["data-testid"]}
        class={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
          "transition-colors cursor-pointer",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
          "data-[expanded]:ring-2 data-[expanded]:ring-inset data-[expanded]:ring-ring",
          local.class,
        )}
      >
        <SelectPrimitive.Value<T> class="block truncate text-left">
          {(state) => (
            <Show
              when={state.selectedOption()}
              keyed
              fallback={<span class="text-muted-foreground">{local.placeholder}</span>}
            >
              {(selected) => <>{label(selected)}</>}
            </Show>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 text-muted-foreground transition-transform duration-200 data-[expanded]:rotate-180">
          <ChevronDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {/* mount: host tagged data-live-announcer so modal Dialog's
          ariaHideOutside DOES NOT hide listbox (CC-21) — see overlay-portal.ts. */}
      <SelectPrimitive.Portal mount={overlayPortalHost()}>
        <SelectPrimitive.Content
          class={cn(
            "z-[250] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl outline-none",
            "ui-select-content",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-[min(18rem,60dvh)] overflow-y-auto p-1.5" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the CTA from
 * `emptyAction` (creation button or navigation link) when present and
 * allowed; otherwise, a dimmed "no options" message. The CTA is keyboard-
 * accessible (native button/anchor) and inherits the panel's opacity-only
 * entry (`ui-select-content`) → respects `prefers-reduced-motion` via the global guard.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-3 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at call site so this primitive remains free
              of i18n; neutral em-dash is the fallback placeholder of last resort. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1">
          <Show when={action().description}>
            <p class="px-3 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
